// src/pages/ClinicalDataCollector/components/output/SpecialistNotesSection.jsx

import React, { useMemo } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import DateRangeIcon from '@mui/icons-material/DateRange';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import { useSelector } from 'react-redux';

import specialistNotesData from '../data/specialistNotesData.json';

// Fallback por si algo sale mal con el JSON
const sampleSpecialistNotes = [
  {
    provider: 'Jean L. Renelien, MD',
    date: '06/20/2025',
    icdCodes: ['I10', 'E78.5'],
    excerpt:
      'Patient’s blood pressure improved on Lisinopril; lipid profile remains elevated, consider intensifying statin therapy.',
  },
  {
    provider: 'Maria Sanchez, PA-C',
    date: '05/30/2025',
    icdCodes: ['J44.9'],
    excerpt:
      'Spirometry shows FEV₁/FVC 55%; continued exacerbations—recommending adding inhaled corticosteroid to regimen.',
  },
  {
    provider: 'Carlos Perez, MD',
    date: '06/15/2025',
    icdCodes: ['I48.0'],
    excerpt:
      'AF management: patient tolerating rate control with Metoprolol, no symptoms of palpitations at rest.',
  },
];

const SpecialistNotesSection = () => {
  const { PatientSelect } = useSelector(
    (state) => state.patientMapSelect || {}
  );

  const patients = specialistNotesData?.patients || [];

  const selectedNotes = useMemo(() => {
    if (!patients.length) {
      return sampleSpecialistNotes;
    }

    // Si hay paciente seleccionado, usamos memberId + age para un índice estable
    if (PatientSelect?.memberId) {
      const base =
        (PatientSelect.memberId?.length || 1) +
        (PatientSelect.age || 0) * 17;
      const idx = Math.abs(base) % patients.length;
      return patients[idx].notes || sampleSpecialistNotes;
    }

    // Si no hay PatientSelect aún, usamos un paciente aleatorio
    const randomIdx = Math.floor(Math.random() * patients.length);
    return patients[randomIdx].notes || sampleSpecialistNotes;
  }, [PatientSelect, patients]);

  return (
    <Card elevation={3} sx={{ borderRadius: 2, mb: 4 }}>
      <CardHeader
        title="Section: Specialist Notes"
        titleTypographyProps={{ variant: 'h5', align: 'center' }}
        action={<LocalHospitalIcon fontSize="large" color="secondary" />}
      />
      <Divider />
      <CardContent>
        <List>
          {selectedNotes.map((note, idx) => (
            <ListItem key={idx} alignItems="flex-start">
              <ListItemIcon>
                <DescriptionIcon />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography
                    variant="subtitle1"
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    {note.provider}
                    <DateRangeIcon fontSize="small" sx={{ mx: 1 }} />
                    {note.date}
                  </Typography>
                }
                secondary={
                  <>
                    <Typography variant="body2" color="text.primary">
                      <strong>Codes:</strong>{' '}
                      {(note.icdCodes || []).join(', ')}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {note.excerpt}
                    </Typography>
                  </>
                }
              />
            </ListItem>
          ))}
        </List>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: 'block' }}
        >
        </Typography>
      </CardContent>
    </Card>
  );
};

export default SpecialistNotesSection;
